import type { FeatureFlag, Tier } from '../schema/features.js';

const PRO_FEATURES: ReadonlySet<FeatureFlag> = new Set<FeatureFlag>([
  'theming',
  'multiProfile',
  'scheduledProfiles',
  'agentActions',
  'diagnosticOverlay',
  // These were in the FeatureFlag schema + Builder Features editor but
  // never entitled at any tier here, which silently dropped them from
  // the license↔config intersection on-device. Tier comparison docs
  // position both as Pro:
  //   - broadcasts:  tiers.md table + faq.md
  //   - customTools: surfaced in Features editor at Pro
  'broadcasts',
  'customTools',
  'biometricAuth',
  // Pure-JS desktop-mode heuristic.
  'dexDetectionBasic',
  // Signage mode — hides launcher chrome (header + footer) and
  // upsizes the tile grid for distance viewing. Pairs naturally with
  // scheduledProfiles for auto-cycling content. Promoted from Enterprise
  // because the use case (one tablet on a wall) is squarely SMB.
  'signageMode',
]);

const ENTERPRISE_FEATURES: ReadonlySet<FeatureFlag> = new Set<FeatureFlag>([
  ...PRO_FEATURES,
  'remoteConfig',
  'pilotRollout',
  'telemetryExport',
  'ssoDisplay',
  'auditLog',
  // Precise DeX detection via the native AndroidBridge.isDexMode().
  // Falls back to the Pro-tier heuristic if the bridge isn't present.
  'dexDetectionNative',
  'oem.zebra',
  'oem.samsung',
  'oem.honeywell',
]);

/**
 * Default feature set per tier. License `features[]` grants are layered on
 * top (the union); `config.enabledFeatures` is then intersected to produce
 * the effective set.
 */
export const TIER_FEATURES: Readonly<Record<Tier, ReadonlySet<FeatureFlag>>> = Object.freeze({
  core: new Set<FeatureFlag>(),
  pro: PRO_FEATURES,
  enterprise: ENTERPRISE_FEATURES,
});
